import { useState } from "react";
import { useLanguage } from "../../../i18n/LanguageContext";

// Components
import RequestTypeSelector from "./RequestTypeSelector";
import ServicesSelector from "./ServicesSelector";
import PackagesSelector from "./PackagesSelector";

// Component from ui
import Button from "../../ui/Button";

export default function RequestForm() {
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: "",
    company: "",
    message: "",
  });

  const [requestType, setRequestType] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const validPhone = (value) => /^\d{10}$/.test(value);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const cleanValue =
      name === "phone" ? value.replace(/[^\d]/g, "").slice(0, 10) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: cleanValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setFormError("");
    setSuccessMessage("");
  };

  const handleRequestTypeChange = (e) => {
    setRequestType(e.target.value);
    setSelectedServices([]);
    setSelectedPackage("");

    setErrors((prev) => ({
      ...prev,
      requestType: "",
      selectedServices: "",
      selectedPackage: "",
    }));

    setFormError("");
    setSuccessMessage("");
  };

  const handleServiceChange = (e) => {
    const { value, checked } = e.target;

    if (checked) {
      setSelectedServices((prev) => [...prev, value]);
    } else {
      setSelectedServices((prev) => prev.filter((item) => item !== value));
    }

    setErrors((prev) => ({
      ...prev,
      selectedServices: "",
    }));

    setFormError("");
    setSuccessMessage("");
  };

  const handlePackageChange = (e) => {
    setSelectedPackage(e.target.value);

    setErrors((prev) => ({
      ...prev,
      selectedPackage: "",
    }));

    setFormError("");
    setSuccessMessage("");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t.request.errors.nameRequired;
    }

    if (!formData.email.trim()) {
      newErrors.email = t.request.errors.emailRequired;
    } else if (!validEmail(formData.email)) {
      newErrors.email = t.request.errors.emailInvalid;
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t.request.errors.phoneRequired;
    } else if (!validPhone(formData.phone)) {
      newErrors.phone = t.request.errors.phoneInvalid;
    }

    if (!formData.projectType.trim()) {
      newErrors.projectType = t.request.errors.projectTypeRequired;
    }

    if (!formData.company.trim()) {
      newErrors.company = t.request.errors.companyRequired;
    }

    if (!requestType) {
      newErrors.requestType = t.request.errors.requestTypeRequired;
    }

    if (requestType === "services" && selectedServices.length === 0) {
      newErrors.selectedServices = t.request.errors.servicesRequired;
    }

    if (requestType === "packages" && !selectedPackage) {
      newErrors.selectedPackage = t.request.errors.packageRequired;
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setFormError(t.request.formError);
      setSuccessMessage("");
      return;
    }

    setFormError("");

    try {
      const response = await fetch("/api/requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: formData.name,
          email: formData.email,
          phone: formData.phone,
          project_type: formData.projectType,
          project_name: formData.company,
          request_type: requestType,
          selected_service: selectedServices.join(", "),
          selected_package: selectedPackage,
          notes: formData.message,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(t.request.success);
        setFormData({ name: "", email: "", phone: "", projectType: "", company: "", message: "" });
        setRequestType("");
        setSelectedServices([]);
        setSelectedPackage("");
        setErrors({});
      } else {
        setFormError("حدث خطأ، حاول مرة ثانية");
      }
    } catch (err) {
      setFormError("تعذّر الاتصال بالسيرفر");
    }
  };

  return (
    <section className="request-card">
      <h2 className="request-title">{t.request.title}</h2>

      <p className="request-desc">{t.request.desc}</p>

      <form className="request-form" onSubmit={handleSubmit}>
        <label className="request-label required">{t.request.labels.name}</label>
        <input
          className="request-input"
          type="text"
          name="name"
          placeholder={t.request.placeholders.name}
          value={formData.name}
          onChange={handleChange}
        />
        {errors.name && <small className="request-error">{errors.name}</small>}

        <label className="request-label required">{t.request.labels.email}</label>
        <input
          className="request-input"
          type="email"
          name="email"
          placeholder={t.request.placeholders.email}
          value={formData.email}
          onChange={handleChange}
        />
        {errors.email && (
          <small className="request-error">{errors.email}</small>
        )}

        <label className="request-label required">{t.request.labels.phone}</label>
        <input
          className="request-input"
          type="text"
          name="phone"
          placeholder={t.request.placeholders.phone}
          value={formData.phone}
          onChange={handleChange}
        />
        {errors.phone && (
          <small className="request-error">{errors.phone}</small>
        )}

        <label className="request-label required">
          {t.request.labels.projectType}
        </label>
        <select
          className="request-input request-select"
          name="projectType"
          value={formData.projectType}
          onChange={handleChange}
        >
          <option value="">{t.request.projectTypes.default}</option>
          <option value="Restaurant">{t.request.projectTypes.restaurant}</option>
          <option value="Coffee Shop">{t.request.projectTypes.coffeeShop}</option>
        </select>
        {errors.projectType && (
          <small className="request-error">{errors.projectType}</small>
        )}

        <label className="request-label required">
          {t.request.labels.company}
        </label>
        <input
          className="request-input"
          type="text"
          name="company"
          placeholder={t.request.placeholders.company}
          value={formData.company}
          onChange={handleChange}
        />
        {errors.company && (
          <small className="request-error">{errors.company}</small>
        )}

        <RequestTypeSelector
          requestType={requestType}
          onChange={handleRequestTypeChange}
        />
        {errors.requestType && (
          <small className="request-error">{errors.requestType}</small>
        )}

        <hr className="request-divider" />

        <div className="request-options">
          {requestType === "services" && (
            <>
              <ServicesSelector
                selectedServices={selectedServices}
                onCheckboxChange={handleServiceChange}
              />
              {errors.selectedServices && (
                <small className="request-error">
                  {errors.selectedServices}
                </small>
              )}
            </>
          )}

          {requestType === "packages" && (
            <>
              <PackagesSelector
                selectedPackage={selectedPackage}
                onChange={handlePackageChange}
              />
              {errors.selectedPackage && (
                <small className="request-error">
                  {errors.selectedPackage}
                </small>
              )}
            </>
          )}
        </div>

        <div className="request-notes">
          <label className="request-label">{t.request.labels.notes}</label>
          <textarea
            className="request-input request-textarea"
            name="message"
            placeholder={t.request.placeholders.notes}
            value={formData.message}
            onChange={handleChange}
          />
        </div>

        {formError && <div className="request-form-error">{formError}</div>}

        <Button
          text={t.request.button.send}
          type="submit"
          className="request-submit-btn"
        />

        {successMessage && (
          <div className="request-success-box">{successMessage}</div>
        )}
      </form>
    </section>
  );
}

